import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import SvgIcon from '@mui/material/SvgIcon'
import { ReactComponent as PinIcon } from '../../../assets/images/actions/pin-filled.svg'
import { TppColumn as TppColumnType, DRAG_TYPES } from '../types'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import TppColumnActions from './TppColumnActions'
import TppColumnLockIcon from './TppColumnLockIcon'
import TppColumnArchiveIcon from './TppColumnArchiveIcon'
import ColumnEditView from './TppColEdit/ColumnEditView'

interface TppColumnProps {
    column: TppColumnType
    isPinned: boolean
    onPinToggle: () => Promise<void>
    onLockToggle: () => Promise<void>
    onArchiveToggle: () => Promise<void>
    onSaveVersionClick?: () => void
    isPinning?: boolean
    isLocking?: boolean
    isArchiving?: boolean
    isArchivedTab?: boolean
    stickyStyle?: React.CSSProperties
    canEdit?: boolean
}

const TppColumn: React.FC<TppColumnProps> = ({
    column,
    isPinned,
    onPinToggle,
    onLockToggle,
    onArchiveToggle,
    onSaveVersionClick,
    isPinning,
    isLocking,
    isArchiving,
    isArchivedTab,
    stickyStyle,
    canEdit = true,
}) => {
    const layout = useLayoutConstants()
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
        id: column._id,
        disabled: true,
        data: { type: DRAG_TYPES.COLUMN, columnId: column._id },
    })

    const [isEditOpen, setIsEditOpen] = React.useState(false)

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
        flex: '0 0 auto',
        width: layout.COLUMN_WIDTH,
        padding: '12px 16px',
        fontWeight: 600,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // cursor: 'grab',
        backgroundColor: 'figma.grayscale.contentWrapper',
        ...stickyStyle,
        ...(stickyStyle?.position === 'sticky' ? { backgroundColor: 'figma.grayscale.tier6', zIndex: 40 } : {}),
    }

    return (
        <>
            <Stack direction="row" ref={setNodeRef} sx={style}>
                <Typography variant="18x600" {...attributes} {...listeners}>
                    {column.title}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                    {column.archived ? (
                        <TppColumnArchiveIcon archived={column.archived} />
                    ) : (
                        !isArchivedTab &&
                        isPinned && (
                            <SvgIcon
                                component={PinIcon}
                                inheritViewBox
                                fontSize="small"
                                color="primary"
                                sx={{ fontSize: '20px' }}
                            />
                        )
                    )}
                    {!isArchivedTab && column.locked && <TppColumnLockIcon locked={column.locked} />}
                    {canEdit && (
                        <TppColumnActions
                            column={column}
                            isPinned={isPinned}
                            onPinToggle={onPinToggle}
                            onLockToggle={onLockToggle}
                            onArchiveToggle={onArchiveToggle}
                            onSaveVersionClick={onSaveVersionClick}
                            isPinning={isPinning}
                            isLocking={isLocking}
                            isArchiving={isArchiving}
                            isArchivedTab={isArchivedTab}
                            onEditClick={() => setIsEditOpen(true)}
                        />
                    )}
                </Stack>
            </Stack>
            <ColumnEditView
                open={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                tppId={column.tppId}
                columnId={column._id}
            />
        </>
    )
}

export default TppColumn
